import { MoreHorizontal, Power, PowerOff, Settings2, Trash2, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { HistorySection } from '@/features/audit-log';
import { useCountryScope, usePermissions } from '@/features/auth';
import { ProviderPerformance } from '@/features/reports';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatCurrency } from '@/shared/lib/format';
import { NON_MARKET_TIME_NOTE } from '@/shared/lib/time-zone';

import { useProvider } from '../api/providers.queries';
import { BalanceApprovalsPanel } from '../components/balance-approvals-panel';
import { AvailabilityPanel } from '../components/availability-panel';
import { ProviderBalance } from '../components/provider-balance';
import { MappingsPanel } from '../components/mappings-panel';
import { StockSyncPanel } from '../components/stock-sync-panel';
import {
  ProviderBalanceAdjustmentDialog,
  ProviderBalanceSettingsDialog,
  ProviderDeleteDialog,
  ProviderStatusDialog,
  providerDeleteBlocker,
} from '../components/provider-dialogs';

type OpenDialog = 'status' | 'settings' | 'adjust' | 'delete' | null;

/**
 * One provider: status, balance and its alarm, and every item it supplies
 * (its mappings). Changes need providers.manage and all-country scope. Only a
 * provider nothing uses can be deleted — the API refuses the rest.
 */
export function ProviderDetailsPage() {
  const navigate = useNavigate();
  const { providerId } = useParams<{ providerId: string }>();
  const parsedId = Number(providerId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;
  const provider = useProvider(isValidId ? parsedId : undefined);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();

  if (!isValidId) return <NotFoundPage />;
  if (provider.isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </PageContainer>
    );
  }
  if (provider.error instanceof ApiError && provider.error.isNotFound) return <NotFoundPage />;
  if (provider.error || !provider.data) {
    return (
      <PageContainer>
        <ErrorState error={provider.error} onRetry={() => void provider.refetch()} />
      </PageContainer>
    );
  }

  const data = provider.data;
  const currency = data.currencyCode ?? 'USD';
  const canManage = can('providers.manage') && allCountries;
  const close = () => setDialog(null);
  const deleteBlocker = providerDeleteBlocker(data);

  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/providers', label: 'Providers' }}
        title={
          <span className="flex items-center gap-2.5">
            {data.name}
            <StatusBadge variant={data.isActive ? 'success' : 'neutral'}>
              {data.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
          </span>
        }
        description={`Supplies ${data.activeMappingCount} items; the only provider for ${data.soleProviderCount} ${NON_MARKET_TIME_NOTE}`}
        actions={
          canManage ? (
            <>
              <Button
                size="sm"
                variant={data.isActive ? 'outline' : 'default'}
                onClick={() => setDialog('status')}
              >
                {data.isActive ? (
                  <PowerOff className="size-3.5" aria-hidden />
                ) : (
                  <Power className="size-3.5" aria-hidden />
                )}
                {data.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              {/* Kept in a menu, away from the common actions: deleting is permanent. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" aria-label="More actions">
                    <MoreHorizontal className="size-3.5" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    onSelect={() => setDialog('delete')}
                    disabled={deleteBlocker !== null}
                    className="gap-2 text-xs"
                    variant="destructive"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    {deleteBlocker ? (
                      <span className="flex flex-col">
                        Delete provider
                        <span className="text-muted-foreground text-[11px]">{deleteBlocker}</span>
                      </span>
                    ) : (
                      'Delete provider'
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : undefined
        }
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm">Prepaid balance</CardTitle>
          {canManage && (
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDialog('settings')}>
                <Settings2 className="size-3.5" aria-hidden />
                Balance settings
              </Button>
              {/* Turned off while the balance is read from the provider itself; the dialog stays for when it is needed. */}
              <Button
                size="sm"
                variant="outline"
                disabled
                title="Turned off: the balance is read from the provider"
                onClick={() => setDialog('adjust')}
              >
                <Wallet className="size-3.5" aria-hidden />
                Adjust balance
              </Button>
            </span>
          )}
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 text-sm sm:grid-cols-3">
            <ProviderBalance provider={data} />
            <div>
              <dt className="text-muted-foreground text-[11px] font-medium uppercase">Alarm at</dt>
              <dd className="mt-1 tabular-nums">
                {data.lowBalanceThreshold === null
                  ? 'No alarm'
                  : formatCurrency(data.lowBalanceThreshold, currency)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-[11px] font-medium uppercase">
                Only used when the balance covers the cost
              </dt>
              <dd className="mt-1">
                {data.isBalanceConsidered
                  ? 'On — checkout skips it for vouchers that cost more than its balance'
                  : 'Off — checkout can pick it even at zero balance'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <ProviderPerformance providerId={data.id} />

      <BalanceApprovalsPanel providerId={data.id} />

      <StockSyncPanel providerId={data.id} canManage={can('providers.manage')} />

      <section aria-labelledby="mappings-heading" className="flex flex-col gap-2">
        <h2 id="mappings-heading" className="text-sm font-semibold">
          Items supplied
        </h2>
        <MappingsPanel filter={{ vendorId: data.id }} />
      </section>

      {can('catalogue.read') && (
        <AvailabilityPanel providerId={data.id} canManage={can('providers.manage')} />
      )}

      <HistorySection entityType="Provider" entityId={String(data.id)} />

      {dialog === 'status' && <ProviderStatusDialog provider={data} onClose={close} />}
      {dialog === 'settings' && <ProviderBalanceSettingsDialog provider={data} onClose={close} />}
      {dialog === 'adjust' && <ProviderBalanceAdjustmentDialog provider={data} onClose={close} />}
      {dialog === 'delete' && (
        <ProviderDeleteDialog
          provider={data}
          onClose={close}
          onDeleted={() => void navigate('/providers', { replace: true })}
        />
      )}
    </PageContainer>
  );
}
